import React, { useState, useEffect, useRef } from 'react'
import ExtractLoader from './ExtractLoader'
import { documentsApi } from '../services/api'
import {
  FileText,
  FileSpreadsheet,
  File,
  Download,
  AlertCircle
} from 'lucide-react'

/**
 * DocumentViewer - Renders non-PDF documents inline using client-side parsing
 * - Excel files (.xlsx, .xls) → SheetJS (xlsx) library → HTML table
 * - Word files (.docx) → mammoth.js → HTML
 * - Legacy Office (.doc, .ppt, .pptx) → Office Online Viewer via SAS URL
 * - Images (.png, .jpg, .gif, etc.) → Inline <img>
 * - Text/CSV (.txt, .csv, .eml) → Styled text viewer
 * - Fallback → Download card
 */
function DocumentViewer({ documentId, document: doc, onDownload, toast }) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [textContent, setTextContent] = useState(null)
  const [imageBlobUrl, setImageBlobUrl] = useState(null)
  const [isFullscreen, setIsFullscreen] = useState(false)
  // Excel state
  const [excelData, setExcelData] = useState(null) // { sheetNames: [], sheets: { name: [[row]] } }
  const [activeSheet, setActiveSheet] = useState(0)
  // Word/docx state
  const [docxHtml, setDocxHtml] = useState(null)
  // Office Online state (for legacy formats: .doc, .ppt, .pptx)
  const [sasUrl, setSasUrl] = useState(null)
  const [iframeKey, setIframeKey] = useState(0)
  const sasRefreshTimerRef = useRef(null)

  const filename = doc?.filename || ''
  const ext = filename.split('.').pop()?.toLowerCase() || ''

  const isExcelFile = ['xlsx', 'xls'].includes(ext)
  const isWordFile = ['docx'].includes(ext)
  const isOfficeOnlineFile = ['doc', 'ppt', 'pptx'].includes(ext) // Legacy formats → Office Online Viewer
  const isImage = ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp', 'svg', 'tiff', 'tif'].includes(ext)
  const isTextFile = ['txt', 'csv', 'eml', 'json', 'xml', 'html', 'htm', 'log', 'md'].includes(ext)

  // Clean up blob URLs on unmount
  useEffect(() => {
    return () => {
      if (imageBlobUrl) window.URL.revokeObjectURL(imageBlobUrl)
    }
  }, [imageBlobUrl])

  // Load content based on file type
  useEffect(() => {
    if (!documentId || !doc) return

    let cancelled = false
    setLoading(true)
    setError(null)
    setTextContent(null)
    setImageBlobUrl(null)
    setExcelData(null)
    setDocxHtml(null)
    setSasUrl(null)
    setActiveSheet(0)

    const loadContent = async () => {
      try {
        if (isExcelFile) {
          // Download file and parse with SheetJS
          const response = await documentsApi.download(documentId)
          if (cancelled) return
          const XLSX = await import('xlsx')
          const arrayBuffer = response.data instanceof ArrayBuffer
            ? response.data
            : await new Blob([response.data]).arrayBuffer()
          const workbook = XLSX.read(arrayBuffer, { type: 'array' })
          const sheets = {}
          for (const name of workbook.SheetNames) {
            const sheet = workbook.Sheets[name]
            sheets[name] = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' })
          }
          if (!cancelled) {
            setExcelData({ sheetNames: workbook.SheetNames, sheets })
          }
        } else if (isWordFile) {
          // Download file and parse with mammoth
          const response = await documentsApi.download(documentId)
          if (cancelled) return
          const mammoth = await import('mammoth')
          const arrayBuffer = response.data instanceof ArrayBuffer
            ? response.data
            : await new Blob([response.data]).arrayBuffer()
          const result = await mammoth.convertToHtml({ arrayBuffer })
          if (!cancelled) {
            setDocxHtml(result.value)
          }
        } else if (isImage) {
          // Fetch image as blob and create object URL
          const response = await documentsApi.download(documentId)
          if (!cancelled) {
            const blob = new Blob([response.data], { type: doc.content_type || 'image/png' })
            setImageBlobUrl(window.URL.createObjectURL(blob))
          }
        } else if (isTextFile) {
          // Fetch text content
          const response = await documentsApi.download(documentId)
          if (!cancelled) {
            const blob = new Blob([response.data])
            const text = await blob.text()
            setTextContent(text)
          }
        } else if (isOfficeOnlineFile) {
          // Legacy Office formats → get SAS URL for Office Online Viewer (8h expiry)
          const res = await documentsApi.getSasUrl(documentId, 8)
          if (!cancelled) {
            setSasUrl(res.data.url)
            setIframeKey(k => k + 1)
          }
        }
      } catch (err) {
        console.error('Error loading document preview:', err)
        if (!cancelled) setError('Failed to load preview')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    loadContent()
    return () => { cancelled = true }
  }, [documentId, doc?.id])

  // Auto-refresh SAS URL every 30 minutes for Office Online files
  useEffect(() => {
    if (sasRefreshTimerRef.current) clearInterval(sasRefreshTimerRef.current)
    if (isOfficeOnlineFile && sasUrl && documentId) {
      sasRefreshTimerRef.current = setInterval(async () => {
        try {
          const res = await documentsApi.getSasUrl(documentId, 8)
          setSasUrl(res.data.url)
          setIframeKey(k => k + 1)
        } catch (err) {
          console.warn('SAS URL auto-refresh failed:', err)
        }
      }, 30 * 60 * 1000) // 30 minutes
    }
    return () => {
      if (sasRefreshTimerRef.current) clearInterval(sasRefreshTimerRef.current)
    }
  }, [isOfficeOnlineFile, sasUrl, documentId])

  const getDocIcon = () => {
    if (filename.endsWith('.pdf')) return FileText
    if (['xlsx', 'xls', 'csv'].includes(ext)) return FileSpreadsheet
    return File
  }

  const getFileTypeLabel = () => {
    const labels = {
      xlsx: 'Excel Spreadsheet', xls: 'Excel Spreadsheet',
      doc: 'Word Document', docx: 'Word Document',
      pptx: 'PowerPoint', ppt: 'PowerPoint',
      csv: 'CSV File', txt: 'Text File',
      msg: 'Outlook Email', eml: 'Email Message',
      json: 'JSON File', xml: 'XML File',
      html: 'HTML File', htm: 'HTML File',
      log: 'Log File', md: 'Markdown File',
      png: 'PNG Image', jpg: 'JPEG Image', jpeg: 'JPEG Image',
      gif: 'GIF Image', bmp: 'Bitmap Image', webp: 'WebP Image',
      svg: 'SVG Image',
    }
    return labels[ext] || `${(ext || '').toUpperCase()} File`
  }

  const formatFileSize = (bytes) => {
    if (!bytes) return null
    if (bytes > 1048576) return `${(bytes / 1048576).toFixed(1)} MB`
    return `${Math.round(bytes / 1024)} KB`
  }

  const handleDownload = async () => {
    try {
      const response = await documentsApi.download(documentId)
      const blob = new Blob([response.data])
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename || 'document'
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (err) {
      console.error('Error downloading:', err)
      toast?.error('Failed to download document')
    }
  }

  // ─── Loading State ───
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full w-full">
        <ExtractLoader size={40} className="mb-3" />
        <p className="text-sm text-gray-500 dark:text-gray-400">Loading document preview...</p>
      </div>
    )
  }

  // ─── Excel Viewer (SheetJS) ───
  if (isExcelFile) {
    if (error || !excelData) {
      return <FallbackCard doc={doc} ext={ext} getDocIcon={getDocIcon} getFileTypeLabel={getFileTypeLabel} formatFileSize={formatFileSize} handleDownload={handleDownload} error={error} />
    }

    const currentSheetName = excelData.sheetNames[activeSheet]
    const rows = excelData.sheets[currentSheetName] || []
    const headerRow = rows[0] || []
    const dataRows = rows.slice(1)
    const maxDisplayRows = 500

    return (
      <div className="flex flex-col h-full w-full">
        {/* Sheet tabs */}
        {excelData.sheetNames.length > 1 && (
          <div className="flex items-center bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700 px-2 gap-0.5 overflow-x-auto flex-shrink-0">
            {excelData.sheetNames.map((name, idx) => (
              <button
                key={name}
                onClick={() => setActiveSheet(idx)}
                className={`px-3 py-1.5 text-[11px] font-medium whitespace-nowrap border-b-2 transition-colors ${
                  idx === activeSheet
                    ? 'border-green-500 text-green-700 dark:text-green-400 bg-white dark:bg-gray-900'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                {name}
              </button>
            ))}
          </div>
        )}

        {/* Table */}
        <div className="flex-1 overflow-auto bg-white dark:bg-gray-900 rounded-b-lg">
          {rows.length === 0 ? (
            <p className="p-4 text-sm text-gray-400 dark:text-gray-500">Empty sheet</p>
          ) : (
            <table className="w-full text-xs border-collapse">
              <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0 z-10">
                <tr>
                  <th className="px-2.5 py-2 text-center font-medium text-gray-400 dark:text-gray-500 border-b border-r border-gray-200 dark:border-gray-700 w-10 bg-gray-100 dark:bg-gray-800 sticky left-0 z-20">#</th>
                  {headerRow.map((h, i) => (
                    <th key={i} className="px-2.5 py-2 text-left font-semibold text-gray-700 dark:text-gray-300 border-b border-r border-gray-200 dark:border-gray-700 whitespace-nowrap">
                      {h !== '' ? String(h) : `Col ${String.fromCharCode(65 + (i % 26))}`}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {dataRows.slice(0, maxDisplayRows).map((row, ri) => (
                  <tr key={ri} className="hover:bg-blue-50/50 dark:hover:bg-gray-800/50 transition-colors">
                    <td className="px-2.5 py-1.5 text-center text-gray-400 dark:text-gray-500 tabular-nums border-r border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/30 sticky left-0">{ri + 1}</td>
                    {headerRow.map((_, ci) => (
                      <td key={ci} className="px-2.5 py-1.5 text-gray-600 dark:text-gray-300 border-r border-b border-gray-100 dark:border-gray-800 whitespace-nowrap max-w-[300px] truncate">
                        {row[ci] !== undefined && row[ci] !== null ? String(row[ci]) : ''}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
              {dataRows.length > maxDisplayRows && (
                <tfoot>
                  <tr>
                    <td colSpan={headerRow.length + 1} className="px-3 py-2 text-center text-xs text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-gray-800">
                      Showing first {maxDisplayRows} of {dataRows.length} rows - download for full data
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          )}
        </div>
      </div>
    )
  }

  // ─── Word (DOCX) Viewer (mammoth.js) ───
  if (isWordFile) {
    if (error || !docxHtml) {
      return <FallbackCard doc={doc} ext={ext} getDocIcon={getDocIcon} getFileTypeLabel={getFileTypeLabel} formatFileSize={formatFileSize} handleDownload={handleDownload} error={error} />
    }

    return (
      <div className="flex flex-col h-full w-full">
        {/* Rendered HTML content */}
        <div className="flex-1 overflow-auto bg-white dark:bg-gray-900 rounded-b-lg">
          <div
            className="docx-content p-6 max-w-4xl mx-auto text-sm text-gray-800 dark:text-gray-200 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: docxHtml }}
          />
        </div>
        {/* Scoped styles for mammoth HTML output */}
        <style>{`
          .docx-content h1 { font-size: 1.5rem; font-weight: 700; margin: 1rem 0 0.5rem; color: inherit; }
          .docx-content h2 { font-size: 1.25rem; font-weight: 600; margin: 0.875rem 0 0.375rem; color: inherit; }
          .docx-content h3 { font-size: 1.1rem; font-weight: 600; margin: 0.75rem 0 0.375rem; color: inherit; }
          .docx-content p { margin: 0.375rem 0; line-height: 1.6; }
          .docx-content ul, .docx-content ol { margin: 0.5rem 0; padding-left: 1.5rem; }
          .docx-content li { margin: 0.25rem 0; }
          .docx-content table { border-collapse: collapse; width: 100%; margin: 0.75rem 0; font-size: 0.8125rem; }
          .docx-content th, .docx-content td { border: 1px solid #e5e7eb; padding: 0.375rem 0.625rem; text-align: left; }
          .dark .docx-content th, .dark .docx-content td { border-color: #374151; }
          .docx-content th { background: #f9fafb; font-weight: 600; }
          .dark .docx-content th { background: #1f2937; }
          .docx-content strong { font-weight: 600; }
          .docx-content em { font-style: italic; }
          .docx-content a { color: #2563eb; text-decoration: underline; }
          .dark .docx-content a { color: #60a5fa; }
          .docx-content img { max-width: 100%; height: auto; margin: 0.5rem 0; border-radius: 0.25rem; }
        `}</style>
      </div>
    )
  }

  // ─── Office Online Viewer (legacy .doc, .ppt, .pptx) ───
  if (isOfficeOnlineFile) {
    if (error || !sasUrl) {
      return <FallbackCard doc={doc} ext={ext} getDocIcon={getDocIcon} getFileTypeLabel={getFileTypeLabel} formatFileSize={formatFileSize} handleDownload={handleDownload} error={error} />
    }

    const officeViewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(sasUrl)}`

    return (
      <div className="flex flex-col h-full w-full">
        <iframe
          key={iframeKey}
          src={officeViewerUrl}
          className="w-full h-full border-0"
          title={`Office Viewer - ${filename}`}
          sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
        />
      </div>
    )
  }

  // ─── Image Viewer ───
  if (isImage) {
    if (loading) {
      return (
        <div className="flex flex-col items-center justify-center h-full w-full">
          <ExtractLoader size={40} className="mb-3" />
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading image...</p>
        </div>
      )
    }

    if (error || !imageBlobUrl) {
      return <FallbackCard doc={doc} ext={ext} getDocIcon={getDocIcon} getFileTypeLabel={getFileTypeLabel} formatFileSize={formatFileSize} handleDownload={handleDownload} error={error} />
    }

    return (
      <div className="flex flex-col h-full w-full">
        {/* Image */}
        <div className="flex-1 overflow-auto flex items-center justify-center p-4 bg-gray-100 dark:bg-gray-800/50 rounded-b-lg" style={{ backgroundImage: 'repeating-conic-gradient(#e5e7eb 0% 25%, transparent 0% 50%)', backgroundSize: '16px 16px' }}>
          <img
            src={imageBlobUrl}
            alt={filename}
            className={`rounded shadow-lg ${isFullscreen ? '' : 'max-w-full max-h-full object-contain'}`}
            style={isFullscreen ? {} : {}}
          />
        </div>
      </div>
    )
  }

  // ─── Text / CSV Viewer ───
  if (isTextFile) {
    if (loading) {
      return (
        <div className="flex flex-col items-center justify-center h-full w-full">
          <ExtractLoader size={40} className="mb-3" />
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading content...</p>
        </div>
      )
    }

    if (error || textContent === null) {
      return <FallbackCard doc={doc} ext={ext} getDocIcon={getDocIcon} getFileTypeLabel={getFileTypeLabel} formatFileSize={formatFileSize} handleDownload={handleDownload} error={error} />
    }

    // For CSV files, try to render as a table
    if (ext === 'csv') {
      return (
        <div className="flex flex-col h-full w-full">
          {/* CSV Table */}
          <div className="flex-1 overflow-auto bg-white dark:bg-gray-900 rounded-b-lg">
            <CsvTable content={textContent} />
          </div>
        </div>
      )
    }

    // Plain text viewer
    return (
      <div className="flex flex-col h-full w-full">
        {/* Text content */}
        <div className="flex-1 overflow-auto bg-white dark:bg-gray-900 rounded-b-lg">
          <pre className="p-4 text-xs leading-relaxed text-gray-700 dark:text-gray-300 font-mono whitespace-pre-wrap break-words">
            {textContent}
          </pre>
        </div>
      </div>
    )
  }

  // ─── Fallback: unsupported type ───
  return <FallbackCard doc={doc} ext={ext} getDocIcon={getDocIcon} getFileTypeLabel={getFileTypeLabel} formatFileSize={formatFileSize} handleDownload={handleDownload} />
}

// ─── CSV Table sub-component ───
function CsvTable({ content }) {
  const lines = content.split('\n').filter(line => line.trim())
  if (lines.length === 0) return <p className="p-4 text-sm text-gray-400">Empty file</p>

  // Simple CSV parser (handles quoted fields)
  const parseLine = (line) => {
    const result = []
    let current = ''
    let inQuotes = false
    for (let i = 0; i < line.length; i++) {
      const ch = line[i]
      if (ch === '"') {
        inQuotes = !inQuotes
      } else if (ch === ',' && !inQuotes) {
        result.push(current.trim())
        current = ''
      } else {
        current += ch
      }
    }
    result.push(current.trim())
    return result
  }

  const headers = parseLine(lines[0])
  const rows = lines.slice(1, 200).map(parseLine) // Limit to 200 rows for performance

  return (
    <table className="w-full text-xs">
      <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
        <tr>
          <th className="px-3 py-2 text-left font-semibold text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700 w-10 text-center">#</th>
          {headers.map((h, i) => (
            <th key={i} className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700 whitespace-nowrap">
              {h || `Col ${i + 1}`}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
        {rows.map((row, ri) => (
          <tr key={ri} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
            <td className="px-3 py-1.5 text-gray-400 dark:text-gray-500 text-center tabular-nums">{ri + 1}</td>
            {headers.map((_, ci) => (
              <td key={ci} className="px-3 py-1.5 text-gray-600 dark:text-gray-300 whitespace-nowrap max-w-[300px] truncate">
                {row[ci] || ''}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
      {lines.length > 201 && (
        <tfoot>
          <tr>
            <td colSpan={headers.length + 1} className="px-3 py-2 text-center text-xs text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-gray-800">
              Showing first 200 of {lines.length - 1} rows
            </td>
          </tr>
        </tfoot>
      )}
    </table>
  )
}

// ─── Fallback Card sub-component ───
function FallbackCard({ doc, ext, getDocIcon, getFileTypeLabel, formatFileSize, handleDownload, error }) {
  const DocIcon = getDocIcon()
  return (
    <div className="flex flex-col items-center justify-center h-full w-full">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-8 flex flex-col items-center max-w-sm text-center">
        <div className="w-16 h-16 rounded-2xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">
          <DocIcon className="h-8 w-8 text-navy-600 dark:text-navy-400" />
        </div>
        <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-0.5 break-all">
          {doc?.filename || 'Untitled Document'}
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
          {getFileTypeLabel()}
        </p>
        {doc?.file_size_bytes && (
          <p className="text-[11px] text-gray-400 dark:text-gray-500 mb-4">
            {formatFileSize(doc.file_size_bytes)}
          </p>
        )}
        {!doc?.file_size_bytes && <div className="mb-4" />}
        {error && (
          <div className="flex items-center gap-1.5 text-xs text-orange-500 dark:text-orange-400 mb-4">
            <AlertCircle className="h-3.5 w-3.5" />
            <span>Preview unavailable - download to view</span>
          </div>
        )}
        {!error && (
          <p className="text-xs text-gray-400 dark:text-gray-500 mb-4">
            Preview is not available for this file type.
          </p>
        )}
        <button
          onClick={handleDownload}
          className="px-5 py-2 bg-navy-800 text-white text-sm font-medium rounded-lg hover:bg-navy-700 transition-colors flex items-center gap-2"
        >
          <Download className="h-4 w-4" />
          Download
        </button>
      </div>
    </div>
  )
}

export default DocumentViewer
